import React, { useState, useEffect } from "react";
import { useSupaContext } from "../../../contexts/SupaContext";
import { Button, Separator, Badge, Flex, Text, Avatar } from "@radix-ui/themes";
import { useNavigate } from "react-router-dom";
import {
  User,
  Mail,
  Phone,
  LogOut,
  Edit,
  Calendar,
  Package,
  Droplets,
  HelpCircle,
  MessageCircle,
} from "lucide-react";
import toast from "react-hot-toast";

const UserProfile = () => {
  const { user, logout } = useSupaContext();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [userRoles, setUserRoles] = useState([]);

  // Extraer datos del usuario del contexto
  const userData = user?.userData || {};
  const email = user?.email || "correo@ejemplo.com";
  const firstName = userData?.first_name || "Usuario";
  const lastName = userData?.last_name || "";
  const phone = userData?.phone || "No registrado";
  const fullName = `${firstName} ${lastName}`.trim();

  useEffect(() => {
    setUserRoles(userData?.roles || ["customer"]);
  }, [userData]);

  // Calcular iniciales para el avatar
  const initials = `${firstName?.[0] || ""}${
    lastName?.[0] || ""
  }`.toUpperCase();

  // Formatear fecha de registro
  const joinDate = user?.created_at
    ? new Date(user.created_at).toLocaleDateString("es-CO", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "Fecha no disponible";

  async function handleLogout() {
    try {
      setIsLoading(true);
      await logout();
      navigate("/");
    } catch (error) {
      toast.error("Error al cerrar sesión");
      console.error("Error al cerrar sesión:", error);
    } finally {
      setIsLoading(false);
    }
  }

  function getRoleBadgeColor(role) {
    switch (role) {
      case "admin":
        return "purple";
      case "customer":
        return "blue";
      case "provider":
        return "green";
      case "contributor":
        return "amber";
      case "superadmin":
        return "crimson";
      default:
        return "gray";
    }
  }

  function getRoleLabel(role) {
    const labels = {
      admin: "Administrador",
      customer: "Cliente",
      provider: "Proveedor",
      contributor: "Colaborador",
      superadmin: "Super Admin",
    };
    return labels[role] || role;
  }

  const handleContactSupport = () => {
    // Aquí puedes implementar la lógica para contactar soporte
    // Por ejemplo: abrir un modal, redirigir a una página de soporte, etc.
    toast.success("Enviando solicitud de soporte...", {
      icon: "📩",
      duration: 3000,
    });
  };

  return (
    <div className="w-full mx-auto rounded-lg">
      {/* Encabezado de perfil simple */}
      <div className="flex flex-col md:flex-row items-center md:justify-between mb-6 gap-8">
        <div className="flex space-x-4 w-full items-center">
          <Avatar
            size="5"
            radius="full"
            fallback={initials}
            className="bg-blue-500 text-white"
          />
          <h1 className="text-xl font-medium text-gray-800">{fullName}</h1>
          </div>
      </div>

      <Separator size="4" mb="6" />

      {/* Información principal - diseño minimalista */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <Text as="h2" size="3" weight="medium">
            Información personal
          </Text>
        </div>

        <div className="space-y-5">
          <div className="flex items-center gap-3">
            <Mail className="text-blue-500" size={18} />
            <div>
              <Text as="div" size="3" weight="medium">
                {email}
              </Text>
              <Text as="div" size="2" color="gray">
                Correo electrónico
              </Text>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Phone className="text-blue-500" size={18} />
            <div>
              <Text as="div" size="3" weight="medium">
                {phone}
              </Text>
              <Text as="div" size="2" color="gray">
                Teléfono
              </Text>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Calendar className="text-blue-500" size={18} />
            <div>
              <Text as="div" size="3" weight="medium">
                {joinDate}
              </Text>
              <Text as="div" size="2" color="gray">
                Cliente desde
              </Text>
            </div>
          </div>
        </div>
      </div>

      <Separator size="4" my="6" />

      {/* Acciones disponibles - fila de botones */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex flex-col sm:flex-row gap-1">
          <Button variant="soft" className="justify-center" size="3">
            <HelpCircle size={16} />
            <span className="">Ayuda</span>
          </Button>
        </div>

        <Button
          color="red"
          variant="soft"
          onClick={handleLogout}
          disabled={isLoading}
          className="justify-center"
          size="3"
        >
          <LogOut size={16} />
          <span className="">
            {isLoading ? "Cerrando sesión..." : "Cerrar sesión"}
          </span>
        </Button>
      </div>
    </div>
  );
};

export default UserProfile;
